<template>
    <!-- Attachment content -->
    <div v-bind:class="panelClassObj">
        <div v-if="store.enableAttachmentAnalysis">
            <!-- Metadata - Only shown here if the attachment analysis feature is enabled -->
            <div>
                <div v-bind:class="metadataRowClass">
                    <div class="col-12 col-md-6">
                        <div class="fw-bold">
                            {{ fileNameLabel }}
                        </div>
                    </div>
                    <div class="col-12 col-md-6">
                        <a
                            data-gtmid="AttachmentView-DownloadOriginal"
                            v-bind:href="store.attachmentDownloadUrl">
                            {{ store.attachmentModel.FileName }}
                        </a>
                    </div>
                </div>
            </div>
            <div
                v-for="{ key, value, valueClass, valueId } in attachmentMetadata"
                v-bind:key="key">
                <div v-bind:class="metadataRowClass">
                    <div class="col-12 col-md-6">
                        <div class="fw-bold">
                            {{ key }}
                        </div>
                    </div>
                    <div class="col-12 col-md-6">
                        <span
                            v-bind:id="valueId"
                            v-bind:class="valueClass">
                            {{ value }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <!-- PDF Content -->
        <div v-if="showAsPdf">
            <div
                v-if="!failedToRetrieveFile"
                class="mb-3">
                <a
                    v-bind:href="store.attachmentContentUrl"
                    target="_blank">
                    {{ openInNewWindowLinkText }}
                </a>
            </div>
            <object
                id="pdf"
                type="application/pdf"
                class="w-100 pdf-content"
                v-bind:data="store.attachmentContentUrl"
                v-on:error="failedToRetrieveFile = true">
                <pc-alert shown>
                    <div v-if="failedToRetrieveFile">
                        <p>
                            {{ pdfNotAvailableLine1 }}
                        </p>
                        <p>
                            <a
                                data-gtmid="AttachmentView-DownloadOriginal"
                                v-bind:href="store.attachmentDownloadUrl">
                                {{ pdfUnsupportedLinkText }}
                            </a>
                        </p>
                    </div>
                    <div v-else>
                        <p>
                            {{ pdfUnsupportedLine1 }}
                        </p>
                        <p>
                            <a
                                data-gtmid="AttachmentView-DownloadOriginal"
                                v-bind:href="store.attachmentDownloadUrl">
                                {{ pdfUnsupportedLinkText }}
                            </a>
                        </p>
                        <p class="mb-0">
                            {{ pdfUnsupportedLine2 }}
                        </p>
                    </div>
                </pc-alert>
            </object>
            <!-- Prevent the PDF from stealing mouse input while dragging -->
            <div
                v-if="store.dragging"
                style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: transparent"></div>
        </div>
        <!-- Image content -->
        <div v-else-if="showAsImage">
            <div class="mb-3 text-center">
                <img
                    id="image"
                    class="image-content shadow"
                    v-bind:src="store.attachmentContentUrl"
                    v-bind:alt="imageContentAltText" />
            </div>
            <div>
                <a
                    data-gtmid="AttachmentView-OpenInNewWindow"
                    v-bind:href="store.attachmentContentUrl"
                    target="_blank">
                    {{ openInNewWindowLinkText }}
                </a>
            </div>
        </div>
        <div v-else-if="!store.hasAttachment">
            <div class="fst-italic text-muted">No attachment has been selected.</div>
        </div>
        <!-- Content we can't show inline -->
        <div v-else>
            <pc-alert
                shown
                class="mb-0">
                <div>
                    <p>
                        {{ unsupportedFileTypeLine1 }}
                    </p>
                    <p class="mb-0">
                        <a
                            data-gtmid="AttachmentView-OpenUnsupportedFile"
                            v-bind:href="store.attachmentDownloadUrl">
                            {{ unsupportedFileTypeDownloadLinkText }}
                        </a>
                    </p>
                </div>
            </pc-alert>
        </div>
    </div>
</template>
<script setup>
    import { ref, computed, inject } from "vue";
    import { useResource } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/attachmentDisplayComponent.i18n";
    import { formatBasicUserInfoForDisplay } from "Util/clientUser";
    import {
        getApprovalStatusDisplay,
        getApprovalStatusDisplayClass,
        ATTACHMENT_CONTENT_DISPLAY_TYPE
    } from "Util/clientAttachment";
    import { useAttachmentViewStore } from "./attachmentViewStore";
    import { PC_RES_SYMBOL } from "_Main/injectionSymbols";

    const res = inject(PC_RES_SYMBOL);
    const store = useAttachmentViewStore();

    // i18n copy
    const {
        fileNameLabel,
        uploadedByLabel,
        uploadTimestampLabel,
        statusLastUpdatedByLabel,
        statusLastUpdatedTimeLabel,
        openInNewWindowLinkText,
        pdfUnsupportedLine1,
        pdfUnsupportedLinkText,
        pdfUnsupportedLine2,
        pdfNotAvailableLine1,
        currentStatusLabel,
        unsupportedFileTypeDownloadLinkText
    } = useResource(KEYS, NAMESPACE);
    const uploadUserDisplay = computed(() =>
        formatBasicUserInfoForDisplay(store.attachmentModel.UploadUser, null, true)
    );
    const imageContentAltText = res({
        namespace: NAMESPACE,
        key: KEYS.imageContentAltText,
        values: {
            fileName: store.attachmentModel?.FileName,
            uploadUserDisplay: uploadUserDisplay.value
        }
    });
    const unsupportedFileTypeLine1 = res({
        namespace: NAMESPACE,
        key: KEYS.unsupportedFileTypeLine1,
        values: {
            fileType: store.attachmentModel.DocumentType
        }
    });

    // data
    const failedToRetrieveFile = ref(false);

    // style
    const panelClassObj = computed(() => ({
        "pe-3": store.enableAttachmentAnalysis,
        "px-3": store.enableAttachmentAnalysis,
        "py-3": !store.enableAttachmentAnalysis
    }));
    const metadataRowClass = ref("row mb-3 mb-md-1");

    // computed
    const reviewerMetadata = computed(() => {
        const baseMetadata = [
            {
                key: currentStatusLabel.value,
                value: getApprovalStatusDisplay(store.attachmentModel?.Status),
                valueClass: getApprovalStatusDisplayClass(store.attachmentModel?.Status),
                valueId: "currentStatus"
            }
        ];

        if (store.attachmentModel?.StatusChangeUser) {
            baseMetadata.push({
                key: statusLastUpdatedByLabel.value,
                value: formatBasicUserInfoForDisplay(store.attachmentModel?.StatusChangeUser, null, true)
            });
            if (store.attachmentModel?.StatusChangeTimestamp) {
                baseMetadata.push({
                    key: statusLastUpdatedTimeLabel.value,
                    value: store.attachmentModel?.StatusChangeTimestamp.Display
                });
            }
        }

        return baseMetadata;
    });
    const attachmentMetadata = computed(() => {
        const baseMetadata = [
            {
                key: uploadedByLabel.value,
                value: uploadUserDisplay.value
            },
            { key: uploadTimestampLabel.value, value: store.attachmentModel?.UploadTimestamp?.Display }
        ];

        if (!store.enableAttachmentAnalysis) {
            // Append the reviewer metadata to baseMetadata
            baseMetadata.push(...reviewerMetadata.value);
        }

        return baseMetadata;
    });
    const showAsImage = computed(() => store.displayType === ATTACHMENT_CONTENT_DISPLAY_TYPE.image);
    const showAsPdf = computed(() => store.displayType === ATTACHMENT_CONTENT_DISPLAY_TYPE.pdf);
</script>
<style lang="scss" scoped>
    .pdf-content {
        height: 70vh;
    }

    .image-content {
        max-width: 100%;
    }
</style>
